
import React, { useState, useEffect } from 'react';
import { 
  User, 
  Link2, 
  Key, 
  Settings, 
  Shield, 
  CreditCard, 
  Brain, 
  Lock, 
  History, 
  LogOut,
  Smartphone,
  RefreshCw,
  Unlink,
  ExternalLink,
  Plus,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  DollarSign,
  BarChart3,
  ShieldCheck,
  Zap,
  Activity,
  ArrowLeft,
  X,
  FileText,
  Download,
  Eye,
  ClipboardList,
  ShieldAlert,
  UserPlus
} from 'lucide-react';
import { motion } from 'motion/react';
import { cn, logInteraction } from '../lib/utils';
import { useAuthenticatedUser, NO_PAPER_ACCOUNT_COPY, RESTRICTED_FEATURE_COPY } from '../hooks/useAuthenticatedUser';
import useViewerPortfolio from '../hooks/useViewerPortfolio';
import DemoProfileReviewCard from './DemoProfileReviewCard';
import useAccountSummary from '../hooks/useAccountSummary';
// 2026-06-06 i-visible-defect-purge — Overview row gates flip on
// _sotConnected, not the stale localStorage authUser.paperAccountConnected.
// 2026-06-06 h-source-of-truth-purge — global date formatting
import { formatDateTime, formatRelativeTime } from '../lib/accountFormatters';
import { apiV1Base } from '../lib/apiConfig';

// Backend allowlists — must match
// api_gateway/portfolio/broker_connection_requests.py exactly.
const REQUESTABLE_PROVIDERS: Array<{ id: string; label: string }> = [
  { id: 'alpaca_paper',              label: 'Alpaca Paper' },
  { id: 'interactive_brokers_paper', label: 'Interactive Brokers Paper / Gateway' },
  { id: 'tradier_api',               label: 'Tradier API' },
  { id: 'schwab_api',                label: 'Schwab API' },
  { id: 'binance_api',               label: 'Binance API (crypto)' },
  { id: 'other_api_capable_broker',  label: 'Other API-capable broker' },
];
const REQUESTABLE_MODES: Array<{ id: 'paper' | 'read_only'; label: string }> = [
  { id: 'paper',     label: 'Paper trading (simulated)' },
  { id: 'read_only', label: 'Read-only (no order execution)' },
];

interface BrokerConnectionRequest {
  request_id: string;
  provider: string;
  requested_mode: string;
  status: string;
  notes?: string | null;
  submitted_at?: string;
  updated_at?: string;
}

// Provider-neutral supported broker model. Real-money connections remain
// disabled until per-user gating lands; paper/api flows are surfaced as
// "coming soon" so the demo user sees the roadmap but cannot trigger a
// live action.
const SUPPORTED_BROKERS: Array<{
  id: string;
  label: string;
  mode: 'paper' | 'live' | 'readonly';
  method: 'OAuth' | 'API key' | 'Gateway' | 'Manual';
  status: 'coming_soon' | 'disabled';
  advisory: string;
}> = [
  { id: 'alpaca_paper',  label: 'Alpaca Paper',                mode: 'paper',    method: 'API key',  status: 'self_connect' as any, advisory: 'Paper-only self-connect: bring your own Alpaca paper API key.' },
  { id: 'ibkr_paper',    label: 'Interactive Brokers Paper',   mode: 'paper',    method: 'Gateway',  status: 'coming_soon', advisory: 'Paper-only via IBKR gateway.' },
  { id: 'tradier_api',   label: 'Tradier API',                 mode: 'paper',    method: 'OAuth',    status: 'coming_soon', advisory: 'API access for paper or read-only.' },
  { id: 'schwab_api',    label: 'Schwab API',                  mode: 'readonly', method: 'OAuth',    status: 'coming_soon', advisory: 'Read-only account data.' },
  { id: 'binance_api',   label: 'Binance API',                 mode: 'readonly', method: 'API key',  status: 'disabled',    advisory: 'Crypto API. Disabled until crypto is enabled for this account.' },
  { id: 'other_adapter', label: 'Other API-capable broker',    mode: 'paper',    method: 'Manual',   status: 'coming_soon', advisory: 'Adapter stub. Contact Adversyn admin to request support.' },
];
import PortfolioSettings from './account/PortfolioSettings';
import BillingSubscription from './account/BillingSubscription';
import DemoOnboardingPreview from './DemoOnboardingPreview';
import SecuritySettings from './SecuritySettings';
import PaperApiConnect from './PaperApiConnect';
import AdminNewAccounts from './AdminNewAccounts';
import { ScrollText } from 'lucide-react';

const SectionHeader = ({ title, icon: Icon }: { title: string, icon: any }) => (
  <div className="flex items-center gap-2 mb-6">
    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-600">
      <Icon size={18} />
    </div>
    <h3 className="text-lg font-black text-[var(--app-text)] uppercase italic tracking-tight">{title}</h3>
  </div>
);

const InfoRow = ({ label, value, subValue, highlight }: { label: string, value: string, subValue?: string, highlight?: boolean }) => (
  <div className="flex items-center justify-between py-3 border-b border-[var(--card-border)] last:border-0">
    <span className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">{label}</span>
    <div className="text-right">
      <div className={cn("text-sm font-black italic", highlight ? "text-emerald-600" : "text-[var(--app-text)]")}>
        {value}
      </div>
      {subValue && <div className="text-[10px] font-bold text-[var(--muted-text)] opacity-60 uppercase">{subValue}</div>}
    </div>
  </div>
);

export default function AccountPortal({ 
  initialTab = 'overview', 
  onBack,
  onLogout,
  tradingProfile,
  onUpdateTradingProfile
}: { 
  initialTab?: string, 
  onBack?: () => void,
  onLogout?: () => void,
  tradingProfile: any,
  onUpdateTradingProfile: (p: any) => void
}) {
  const authUser = useAuthenticatedUser();
  // User-scoped broker connections (WO4). Calls GET /api/v1/user/portfolio
  // which the backend filters by bearer-auth user_id and excludes all
  // internal Adversyn paper/simulation accounts.
  const viewerPortfolio = useViewerPortfolio();
  const userConnections = viewerPortfolio.data?.connections ?? [];
  const hasConnectedBroker = userConnections.length > 0
    && Boolean(viewerPortfolio.data?.paper_account_connected);
  const [activeSubTab, setActiveSubTab] = useState(initialTab);
  // demo-broker-connection-request-flow (2026-06-05) — broker request
  // form state. Posts to POST /api/v1/user/broker-connections/request,
  // refreshes from GET .../requests on mount + after each submit. No
  // credential capture; provider + mode + optional notes only.
  const [brokerRequestOpen, setBrokerRequestOpen] = useState(false);
  // WO7 auto-open self-connect form when navigated via ?connect=alpaca_paper.
  React.useEffect(() => {
    let raw: string | null = null;
    try { raw = sessionStorage.getItem('adversyn_account_autoconnect'); } catch { raw = null; }
    if (!raw) return;
    try { sessionStorage.removeItem('adversyn_account_autoconnect'); } catch {}
    if (raw === 'alpaca_paper' || raw === 'alpaca-paper') {
      setActiveSubTab('brokerage');
      setBrokerRequestProvider('alpaca_paper');
      setBrokerRequestOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [brokerRequests, setBrokerRequests] = useState<BrokerConnectionRequest[]>([]);
  const [brokerRequestProvider, setBrokerRequestProvider] = useState<string>('alpaca_paper');
  const [brokerRequestMode, setBrokerRequestMode] = useState<'paper' | 'read_only'>('paper');
  const [brokerRequestNotes, setBrokerRequestNotes] = useState<string>('');
  const [brokerRequestSubmitting, setBrokerRequestSubmitting] = useState<boolean>(false);
  const [brokerRequestError, setBrokerRequestError] = useState<string | null>(null);
  const [brokerRequestLastSubmitted, setBrokerRequestLastSubmitted] = useState<BrokerConnectionRequest | null>(null);

  // demo-alpaca-paper-self-connect (2026-06-05) — local-only state. Credentials
  // are held in React state ONLY during the in-flight POST and are cleared in
  // the finally block of submitAlpacaPaperConnect. They are NEVER persisted in
  // localStorage/sessionStorage/IndexedDB/cookies, and NEVER logged.
  const [alpacaPaperApiKey, setAlpacaPaperApiKey] = useState<string>('');
  const [alpacaPaperSecretKey, setAlpacaPaperSecretKey] = useState<string>('');
  const [alpacaPaperAccountLabel, setAlpacaPaperAccountLabel] = useState<string>('');
  const [alpacaPaperSubmitting, setAlpacaPaperSubmitting] = useState<boolean>(false);
  const [alpacaPaperError, setAlpacaPaperError] = useState<string | null>(null);
  const [alpacaPaperSuccess, setAlpacaPaperSuccess] = useState<{
    key_last4?: string;
    account_label?: string;
    account_number_masked?: string;
    status?: string;
    validation_status?: string;
  } | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);
  // 2026-06-07 sitewide-refresh — Sync Now state for /account?tab=brokerage.
  const [brokerageSyncing, setBrokerageSyncing] = useState(false);
  const [brokerageSyncError, setBrokerageSyncError] = useState<string | null>(null);
  const [brokerageSyncOkAt, setBrokerageSyncOkAt] = useState<string | null>(null);

  const handleBrokerageSync = React.useCallback(async () => {
    if (brokerageSyncing) return;
    setBrokerageSyncing(true);
    setBrokerageSyncError(null);
    try {
      const token = typeof localStorage !== 'undefined'
        ? localStorage.getItem('adversyn_auth_token') : null;
      if (!token) { setBrokerageSyncError('not authenticated'); return; }
      let syncOk = false;
      try {
        const r = await fetch(apiV1Base() + '/user/broker-connections/alpaca-paper/sync', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'omit',
        });
        syncOk = r.ok;
        if (!r.ok) setBrokerageSyncError(`HTTP ${r.status}`);
      } catch (e: any) { setBrokerageSyncError(String(e?.message || e)); }
      // /user/portfolio carries broker_connection_summary + connections[]
      // so a single refetch updates both the Active Connection card and balances.
      try { (viewerPortfolio as any)?.refetch?.(); } catch { /* ignore */ }
      try {
        const evts = await import('../lib/refreshEvents');
        evts.emitRefresh('brokerage', { syncOk });
        evts.emitRefresh('portfolio', { syncOk });
      } catch { /* ignore */ }
      if (syncOk) setBrokerageSyncOkAt('Updated just now');
    } finally {
      setBrokerageSyncing(false);
    }
  }, [brokerageSyncing, viewerPortfolio]);


  const fetchBrokerRequests = React.useCallback(async () => {
    try {
      const token = typeof localStorage !== 'undefined'
        ? localStorage.getItem('adversyn_auth_token') : null;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token && token.trim()) headers['Authorization'] = `Bearer ${token.trim()}`;
      const resp = await fetch(`${apiV1Base()}/user/broker-connections/requests`, { headers });
      if (!resp.ok) return;
      const body = await resp.json();
      const data = (body && body.data) || {};
      const reqs: BrokerConnectionRequest[] = Array.isArray(data.requests) ? data.requests : [];
      setBrokerRequests(reqs);
    } catch { /* swallow; render empty */ }
  }, []);

  useEffect(() => { fetchBrokerRequests(); }, [fetchBrokerRequests]);

  // M-admin-approval 2026-06-05 — surface the LATEST open-ish request
  // so the user can see admin-decision progress. Order of precedence:
  //   pending_admin_setup            → "Broker connection request pending"
  //   approved_pending_secure_setup  → "Request approved — secure setup pending"
  //   rejected                       → "Request rejected"
  //   cancelled                      → "Request cancelled"
  // provisioned_connected is handled by the upper Active Connection
  // block (it would have its own user_broker_connections row), so it
  // doesn't appear in the empty-state card.
  const STATUS_DISPLAY: Record<string, string> = {
    'pending_admin_setup':           'Broker connection request pending',
    'approved_pending_secure_setup': 'Request approved — secure setup pending',
    'rejected':                      'Request rejected',
    'cancelled':                     'Request cancelled',
  };
  const STATUS_BODY: Record<string, string> = {
    'pending_admin_setup':           'An Adversyn admin will review your request and either approve it (followed by secure backend provisioning) or reject it. You will see the result here.',
    'approved_pending_secure_setup': 'An Adversyn admin approved your request. Secure backend provisioning is still required before any broker connection becomes active — Adversyn does not collect API keys in the browser.',
    'rejected':                      'An Adversyn admin reviewed and rejected this request. You can submit a new one for a different provider or mode if needed.',
    'cancelled':                     'This request was cancelled.',
  };
  const STATUS_TONE: Record<string, string> = {
    'pending_admin_setup':           'text-amber-600 border-amber-500/30',
    'approved_pending_secure_setup': 'text-emerald-600 border-emerald-500/30',
    'rejected':                      'text-rose-600 border-rose-500/30',
    'cancelled':                     'text-[var(--muted-text)] border-[var(--input-border)]',
  };
  const STATUS_PRIORITY = ['pending_admin_setup', 'approved_pending_secure_setup', 'rejected', 'cancelled'];
  const pendingBrokerRequest = (STATUS_PRIORITY
    .map(s => brokerRequests.find(r => r.status === s))
    .find(Boolean)) || null;

  const submitBrokerRequest = React.useCallback(async () => {
    setBrokerRequestSubmitting(true);
    setBrokerRequestError(null);
    try {
      const token = typeof localStorage !== 'undefined'
        ? localStorage.getItem('adversyn_auth_token') : null;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token && token.trim()) headers['Authorization'] = `Bearer ${token.trim()}`;
      // Body contains provider + requested_mode + optional notes ONLY.
      // No credential fields — the backend would reject the request
      // with CREDENTIAL_FIELDS_FORBIDDEN if any leaked through.
      const body = {
        provider: brokerRequestProvider,
        requested_mode: brokerRequestMode,
        notes: brokerRequestNotes.trim() || undefined,
      };
      const resp = await fetch(`${apiV1Base()}/user/broker-connections/request`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      const respBody = await resp.json();
      if (!resp.ok || !respBody?.ok) {
        const msg = respBody?.error?.message
          || respBody?.error?.code
          || `Request failed (HTTP ${resp.status})`;
        setBrokerRequestError(String(msg));
        return;
      }
      const created = (respBody.data || {}) as BrokerConnectionRequest;
      setBrokerRequestLastSubmitted(created);
      setBrokerRequestNotes('');
      await fetchBrokerRequests();
    } catch (e: any) {
      setBrokerRequestError(e?.message || 'Network error submitting the request');
    } finally {
      setBrokerRequestSubmitting(false);
    }
  }, [brokerRequestProvider, brokerRequestMode, brokerRequestNotes, fetchBrokerRequests]);

  // demo-alpaca-paper-self-connect (2026-06-05) — bearer-auth POST to the
  // self-connect endpoint. Credentials are sent ONCE; on any outcome the
  // local state is wiped in finally. Errors never echo back the submitted
  // key/secret. On success, refresh the viewer portfolio so the new
  // connection card renders without a hard reload.
  const submitAlpacaPaperConnect = React.useCallback(async () => {
    setAlpacaPaperSubmitting(true);
    setAlpacaPaperError(null);
    setAlpacaPaperSuccess(null);
    // Capture into locals so the in-flight request still has the values
    // even after we clear React state in finally.
    const apiKeyLocal = alpacaPaperApiKey;
    const secretKeyLocal = alpacaPaperSecretKey;
    const accountLabelLocal = alpacaPaperAccountLabel.trim();
    try {
      const token = typeof localStorage !== 'undefined'
        ? localStorage.getItem('adversyn_auth_token') : null;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token && token.trim()) headers['Authorization'] = 'Bearer ' + token.trim();
      const body = {
        api_key: apiKeyLocal,
        secret_key: secretKeyLocal,
        account_label: accountLabelLocal || undefined,
      };
      const resp = await fetch(apiV1Base() + '/user/broker-connections/alpaca-paper/connect', {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      let respBody: any = null;
      try { respBody = await resp.json(); } catch { respBody = null; }
      if (resp.status === 401 || resp.status === 403) {
        setAlpacaPaperError('Alpaca rejected the credentials. Double-check the key/secret pair and try again.');
        return;
      }
      if (resp.status === 400) {
        const msg = respBody?.error?.message
          || respBody?.error?.code
          || respBody?.message
          || 'Bad request';
        setAlpacaPaperError(String(msg));
        return;
      }
      if (!resp.ok || !(respBody && (respBody.ok || respBody.data || respBody.connection_id))) {
        setAlpacaPaperError('Connection failed. No credentials were stored.');
        return;
      }
      // WO11 gate-on-api-confirmation (2026-06-06).
      // POST returned 200, but that is NOT proof of a live connection row.
      // Poll GET /user/broker-connections (the authoritative source) for
      // up to ~6 seconds. Only when the API returns a row with
      // provider_label=alpaca_paper AND status=connected do we set
      // alpacaPaperSuccess. The success state is therefore derived from
      // the live API row, not from the POST response.
      const data = respBody.data || respBody;
      const expectedKeyLast4 = data?.key_last4 || undefined;
      const refetchFn = (viewerPortfolio as any)?.refetch;
      let confirmed: any = null;
      for (let attempt = 0; attempt < 6 && !confirmed; attempt++) {
        await new Promise(r => setTimeout(r, 1000));
        if (typeof refetchFn === 'function') {
          try { refetchFn(); } catch { /* noop */ }
        }
        try {
          const tok = typeof localStorage !== 'undefined' ? localStorage.getItem('adversyn_auth_token') : null;
          const bcResp = await fetch(apiV1Base() + '/user/broker-connections', {
            headers: { ...(tok ? { Authorization: 'Bearer ' + tok } : {}) },
          });
          if (bcResp.ok) {
            const bcBody = await bcResp.json().catch(() => null);
            const conns = (bcBody?.data?.connections || []) as Array<any>;
            const match = conns.find((c: any) =>
              (c.provider_label === 'alpaca_paper') &&
              (String(c.status).toLowerCase() === 'connected'),
            );
            if (match) confirmed = match;
          }
        } catch { /* noop, retry */ }
      }
      if (!confirmed) {
        setAlpacaPaperError(
          'Connection was submitted, but no active broker row was returned. ' +
          'Please retry or contact support if this persists.'
        );
        return;
      }
      setAlpacaPaperSuccess({
        key_last4: confirmed.key_last4 || expectedKeyLast4,
        account_label: confirmed.account_label || data?.account_label || accountLabelLocal || undefined,
        account_number_masked: confirmed.account_number_masked || data?.account_number_masked,
        status: confirmed.status,
        validation_status: confirmed.validation_status || data?.validation_status,
      });
    } catch (e: any) {
      // Never include e.message verbatim if it might echo a payload; use
      // a generic copy. Browser fetch errors don't normally leak request
      // bodies, but we stay defensive.
      setAlpacaPaperError('Connection failed. No credentials were stored.');
    } finally {
      // Hard rule: clear credentials from React state immediately,
      // regardless of outcome. Account label can persist for the next
      // attempt; the key/secret cannot.
      setAlpacaPaperApiKey('');
      setAlpacaPaperSecretKey('');
      setAlpacaPaperSubmitting(false);
    }
  }, [alpacaPaperApiKey, alpacaPaperSecretKey, alpacaPaperAccountLabel, viewerPortfolio]);

  const disconnectBrokerConnection = React.useCallback(async (connectionId: string) => {
    if (!connectionId) return;
    setDisconnectingId(connectionId);
    try {
      const token = typeof localStorage !== 'undefined'
        ? localStorage.getItem('adversyn_auth_token') : null;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token && token.trim()) headers['Authorization'] = 'Bearer ' + token.trim();
      const resp = await fetch(apiV1Base() + '/user/broker-connections/' + encodeURIComponent(connectionId) + '/disconnect', {
        method: 'POST', headers,
      });
      if (resp.ok) {
        try { (viewerPortfolio as any)?.refetch?.(); } catch { /* noop */ }
      }
    } catch { /* swallow; user can retry */ }
    finally {
      setDisconnectingId(null);
    }
  }, [viewerPortfolio]);

  const [viewingDoc, setViewingDoc] = useState<string | null>(null);
  const [analysisDepth, setAnalysisDepth] = useState('Institutional');
  const [aiToggles, setAiToggles] = useState({
    strategy: true,
    coaching: true,
    sentiment: true
  });
  const [riskPermissions, setRiskPermissions] = useState({
    shortSelling: true,
    options: true,
    margin: true,
    afterHours: true
  });

  const legalDocs = [
    { 
      id: 'trading-disclaimer',
      title: 'Trading Disclaimer', 
      desc: 'Risk acknowledgment and platform limitations.', 
      date: 'Updated: Oct 2025',
      content: `
        <h3>1. High Risk Warning</h3>
        <p>Trading stocks, options, futures, and cryptocurrencies involves substantial risk of loss and is not suitable for every investor. The valuation of financial instruments may fluctuate, and as a result, clients may lose more than their original investment.</p>
        
        <h3>2. Loss of Capital</h3>
        <p>All trading strategies are used at your own risk. You should only trade with capital that you can afford to lose. Past performance is not indicative of future results. Adversyn Trading does not guarantee any specific outcome or profit.</p>
        
        <h3>3. No Financial Advice</h3>
        <p>The content provided through Adversyn Trading, including AI-generated analysis, council debates, and strategy suggestions, is for informational purposes only. It does not constitute financial, investment, legal, or tax advice.</p>
        
        <h3>4. AI Analysis Limitations</h3>
        <p>Our AI Council and analysis tools use complex algorithms to process market data. However, AI can make mistakes, hallucinate data, or fail to account for "black swan" events.</p>
      `
    },
    { 
      id: 'risk-disclosure',
      title: 'Risk Disclosure', 
      desc: 'Detailed breakdown of financial trading risks.', 
      date: 'Updated: Oct 2025',
      content: `
        <h3>1. Market Volatility</h3>
        <p>Financial markets are subject to rapid and significant price fluctuations. These fluctuations can be caused by economic news, geopolitical events, or changes in market sentiment.</p>
        
        <h3>2. Liquidity Risk</h3>
        <p>Some financial instruments may become difficult to buy or sell at a desired price due to low trading volume. This can result in significant slippage or the inability to exit a position.</p>
        
        <h3>3. Leverage Risk</h3>
        <p>The use of leverage can magnify both gains and losses. While it allows for larger positions with less capital, it also increases the risk of a total loss of investment.</p>
        
        <h3>4. Operational Risk</h3>
        <p>Trading relies on complex technology and communication systems. System failures, connectivity issues, or cyber-attacks can disrupt trading activities and lead to financial loss.</p>
      `
    },
    { 
      id: 'tos',
      title: 'Terms of Service', 
      desc: 'Platform usage agreement and legal framework.', 
      date: 'Updated: Sep 2025',
      content: `
        <h3>1. Acceptance of Terms</h3>
        <p>By accessing or using the Adversyn Trading platform, you agree to be bound by these Terms of Service and all applicable laws and regulations.</p>
        
        <h3>2. User Conduct</h3>
        <p>Users are responsible for maintaining the confidentiality of their account credentials and for all activities that occur under their account. Prohibited activities include unauthorized access, data mining, and interference with platform operations.</p>
        
        <h3>3. Intellectual Property</h3>
        <p>All content, software, and technology provided through the platform are the property of Adversyn Trading or its licensors and are protected by intellectual property laws.</p>
        
        <h3>4. Limitation of Liability</h3>
        <p>Adversyn Trading shall not be liable for any direct, indirect, incidental, or consequential damages arising from the use of the platform or any trading activities conducted through it.</p>
      `
    },
    { 
      id: 'privacy',
      title: 'Privacy Policy', 
      desc: 'Data handling and institutional privacy standards.', 
      date: 'Updated: Sep 2025',
      content: `
        <h3>1. Data Collection</h3>
        <p>We collect personal information, trading data, and technical information to provide and improve our services. This includes account details, transaction history, and device identifiers.</p>
        
        <h3>2. Use of Information</h3>
        <p>Collected data is used for account management, service optimization, security monitoring, and compliance with legal obligations. We do not sell your personal data to third parties.</p>
        
        <h3>3. Data Security</h3>
        <p>We implement industry-standard security measures to protect your data from unauthorized access, disclosure, or alteration. This includes encryption, firewalls, and regular security audits.</p>
        
        <h3>4. Third-party Sharing</h3>
        <p>We may share information with trusted third-party service providers, such as brokerages and data processors, only as necessary to provide our services.</p>
      `
    },
  ];

  const menuItems = [
    { id: 'overview', label: 'Account Overview', icon: User },
    { id: 'onboarding-preview', label: 'Onboarding Preview', icon: Eye },
    { id: 'brokerage', label: 'Connected Brokerage', icon: Link2 },
    { id: 'api', label: 'API Integrations', icon: Key },
    { id: 'paper-api', label: 'Paper API', icon: ClipboardList },
    { id: 'portfolio', label: 'Portfolio Settings', icon: BarChart3 },
    { id: 'risk', label: 'Risk & Permissions', icon: ShieldCheck },
    { id: 'billing', label: 'Billing & Subscription', icon: CreditCard },
    { id: 'security', label: 'Security Settings', icon: Lock },
    { id: 'admin-review', label: 'Admin · New Accounts', icon: ShieldAlert },
    { id: 'ai', label: 'AI Configuration', icon: Brain },
    { id: 'legal', label: 'Legal & Disclosures', icon: ScrollText },
  ].filter(item =>
    // 2026-06-07 sitewide-refresh-and-billing-restore — Billing menu
    // item must always be visible. The page itself renders an honest
    // demo-safe empty state when no billing backend exists. API + admin
    // surfaces still gate by permission.
    !(item.id === 'api' && !authUser.permissions.api_integrations) &&
    !(item.id === 'paper-api' && !authUser.permissions.api_integrations) &&
    !(item.id === 'admin-review' && !authUser.permissions.admin_review)
  );

  // 2026-06-06 f1-demo-profile-review-card — single source of truth
  const _sot = useAccountSummary();
  const _sotConnected = _sot.brokerConnected;
  const _sotSynced = _sotConnected && _sot.syncStatus === 'synced';
  const renderSubContent = () => {
    switch (activeSubTab) {
      case 'overview':
        return (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
                <SectionHeader title="Account Summary" icon={User} />
                <div className="space-y-1">
                  {/* R4b — demo-aware Account Summary. Hardcoded fake values
                      shown only when a paper account is actually connected.
                      Demo users see their own identity + a connect prompt. */}
                  <InfoRow label="Name" value={authUser.firstName + ' ' + authUser.lastName} />
                  <InfoRow label="Account ID" value={authUser.accountId || 'Pending — assigned after approval'} />
                  <InfoRow label="Account Status" value={authUser.accountStatusLabel} highlight />
                  <InfoRow label="Paper Account" value={(_sotConnected || authUser.paperAccountConnected) ? 'Alpaca Paper · Connected' : 'Not connected'} highlight={Boolean(_sotConnected || authUser.paperAccountConnected)} />
                  {(_sotConnected || authUser.paperAccountConnected) && (
                    <>
                      <InfoRow label="Live brokerage" value="Not enabled" />
                      <InfoRow label="Sync status" value={_sot.display.syncStatus} />
                    </>
                  )}
                  <InfoRow label="Base Currency" value="USD" />
                  {(_sotConnected || authUser.paperAccountConnected) && (
                    // WO8 no-fake-values (2026-06-05) — prior code rendered
                    // fixed mock values regardless of whether the backend
                    // sync had actually populated them. Now show "—"
                    // placeholders until /api/v1/user/portfolio surfaces
                    // real balances (positions_unavailable_pending_sync
                    // contract from WO5).
                    <>
                      <InfoRow label="Buying Power" value={_sotConnected ? _sot.display.buyingPower : "—"} />
                      <InfoRow label="Cash" value={_sotConnected ? _sot.display.cash : "—"} />
                      <InfoRow label="Equity" value={_sotConnected ? _sot.display.equity : "—"} />
                      <InfoRow label="Portfolio Value" value={_sotConnected ? _sot.display.portfolioValue : "—"} />
                      <InfoRow label="Day P&L" value={_sotConnected ? _sot.display.dailyPl : "—"} />
                      <InfoRow label="Day P&L %" value={_sotConnected ? _sot.display.dailyPlPct : "—"} />
                      <InfoRow label="Account #" value={_sotConnected ? _sot.display.accountNumberMasked : "—"} />
                      <InfoRow label="Last Synced" value={_sotConnected ? _sot.display.lastSyncedAt : "—"} />
                      {_sotConnected && !_sotSynced && (
                        <div data-testid="account-summary-sync-pending" className="mt-2 p-2 rounded-lg border border-amber-500/30 bg-amber-500/10 text-[10px] font-bold text-amber-700 uppercase tracking-widest">
                          Broker connected · Waiting for Alpaca balance sync ({_sot.display.syncStatus.toLowerCase()})
                        </div>
                      )}
                      {_sotSynced && (
                        <div data-testid="account-summary-synced-banner" className="mt-2 p-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-[10px] font-bold text-emerald-700 uppercase tracking-widest">
                          Alpaca Paper · Synced · Source of truth: /api/v1/user/portfolio
                        </div>
                      )}
                    </>
                  )}
                  {!(_sotConnected || authUser.paperAccountConnected) && (
                    <div className="mt-4 p-3 rounded-xl border border-[var(--card-border)] bg-[var(--input-bg)] text-xs space-y-1">
                      <div className="font-black uppercase italic text-[var(--app-text)]">{NO_PAPER_ACCOUNT_COPY.heading}</div>
                      <div className="text-[var(--muted-text)]">{NO_PAPER_ACCOUNT_COPY.body}</div>
                      <div className="text-[10px] uppercase tracking-widest text-rose-500/80 font-bold">{NO_PAPER_ACCOUNT_COPY.liveDisabled}</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
                <SectionHeader title="Performance Metrics" icon={Activity} />
                <div className="space-y-1">
                  {/* R4b — performance metrics only shown when a paper account is
                      connected. Demo users get a 'not available' placeholder. */}
                  {!(_sotConnected || authUser.paperAccountConnected) && (
                    <div className="p-3 rounded-xl border border-[var(--card-border)] bg-[var(--input-bg)] text-xs text-[var(--muted-text)]">
                      Performance metrics become available after a paper account is connected.
                    </div>
                  )}
                  {(_sotConnected || authUser.paperAccountConnected) && (<>
                  {/* WO8 no-fake-values — Performance metrics also placeholdered
                      until the backend adapter syncs real fills. */}
                  <InfoRow label="Realized P&L (YTD)" value="—" />
                  <InfoRow label="Unrealized P&L" value="—" />
                  <InfoRow label="Win Rate" value="—" />
                  <InfoRow label="Sharpe Ratio" value="—" />
                  <InfoRow label="Max Drawdown" value="—" />
                  <InfoRow label="Avg Trade Duration" value="—" />
                  <div className="mt-2 p-2 rounded-lg border border-[var(--card-border)] bg-[var(--input-bg)] text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                    Awaiting paper sync · Performance metrics populate after the first synced fill.
                  </div>
                  </>)}
                </div>
              </div>
            </div>
            <DemoProfileReviewCard />
          </div>
        );
      case 'brokerage':
        return (
          <div className="space-y-6" data-testid="brokerage-tab">
            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <SectionHeader title="Active Connection" icon={Link2} />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="brokerage-sync-now"
                    onClick={() => { void handleBrokerageSync(); }}
                    disabled={brokerageSyncing || !hasConnectedBroker}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border border-[var(--card-border)] bg-[var(--input-bg)] hover:bg-[var(--card-border)]/40 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={hasConnectedBroker ? 'Sync connected brokerage now' : 'Connect a brokerage first'}
                  >
                    <RefreshCw size={12} className={brokerageSyncing ? 'animate-spin' : ''} />
                    {brokerageSyncing ? 'Refreshing…' : 'Sync Now'}
                  </button>
                  {!brokerageSyncing && brokerageSyncOkAt && !brokerageSyncError && (
                    <span data-testid="brokerage-sync-success" className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">{brokerageSyncOkAt}</span>
                  )}
                </div>
              </div>
              {brokerageSyncError && (
                <div data-testid="brokerage-sync-error" className="mb-3 px-3 py-2 rounded-lg border border-rose-500/30 bg-rose-500/10 text-[10px] font-black uppercase tracking-widest text-rose-700">
                  Refresh failed: {brokerageSyncError}
                </div>
              )}
              {hasConnectedBroker ? (
                <div data-testid="brokerage-connected-list" className="space-y-3">
                  {userConnections.map((c, i) => (
                    <div
                      key={c.connection_id || `conn-${i}`}
                      data-testid={`brokerage-connection-${c.connection_id || i}`}
                      className="flex items-center justify-between p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[var(--card-bg)] rounded-lg flex items-center justify-center border border-emerald-500/20 shadow-sm">
                          <Link2 size={20} />
                        </div>
                        <div>
                          <h4 className="text-sm font-black text-[var(--app-text)] uppercase italic">
                            {c.provider_label || 'Connected provider'}
                          </h4>
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">
                              {(c.status || 'connected')}
                            </span>
                            {/* WO3 secure-broker-provisioning */}
                            {c.mode && (
                              <span data-testid="brokerage-conn-mode" className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest border border-[var(--card-border)] rounded-md px-2 py-0.5">
                                Mode: {c.mode}
                              </span>
                            )}
                            {c.validation_status && (
                              <span data-testid="brokerage-conn-validation" className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest border border-[var(--card-border)] rounded-md px-2 py-0.5">
                                {c.validation_status === 'validated'
                                  ? 'Validated'
                                  : c.validation_status === 'credential_present_not_probed'
                                  ? 'Credentials present (not probed)'
                                  : c.validation_status === 'validation_failed'
                                  ? 'Validation failed'
                                  : 'Pending validation'}
                              </span>
                            )}
                            {(c as any).live_trading === 0 && (
                              <span className="text-[10px] font-bold text-amber-600 uppercase tracking-widest border border-amber-500/30 rounded-md px-2 py-0.5">
                                Paper only
                              </span>
                            )}
                            {(c as any).key_last4 && (
                              <span
                                data-testid={`brokerage-conn-key-last4-${c.connection_id || i}`}
                                className="text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest border border-[var(--card-border)] rounded-md px-2 py-0.5 font-mono"
                              >
                                {'Key ····' + (c as any).key_last4}
                              </span>
                            )}
                          </div>
                          {(c as any).account_label && (
                            <div
                              data-testid={`brokerage-conn-account-label-${c.connection_id || i}`}
                              className="text-[10px] font-bold text-[var(--muted-text)] mt-1"
                            >
                              {(c as any).account_label}
                            </div>
                          )}
                          {(c as any).account_number_masked && (
                            <div
                              data-testid={`brokerage-conn-account-number-${c.connection_id || i}`}
                              className="text-[10px] font-mono text-[var(--muted-text)] mt-1"
                            >
                              {(c as any).account_number_masked}
                            </div>
                          )}
                        </div>
                      </div>
                      {c.connection_id && (
                        <button
                          type="button"
                          data-testid={`brokerage-disconnect-${c.connection_id}`}
                          onClick={() => { void disconnectBrokerConnection(c.connection_id as string); }}
                          disabled={disconnectingId === c.connection_id}
                          className={cn(
                            "px-3 py-2 rounded-lg text-[10px] font-black uppercase italic tracking-widest border",
                            disconnectingId === c.connection_id
                              ? "bg-[var(--input-bg)] border-[var(--input-border)] text-[var(--muted-text)] opacity-60 cursor-wait"
                              : "bg-[var(--input-bg)] border-rose-500/30 text-rose-600 hover:bg-rose-500/10"
                          )}
                        >{disconnectingId === c.connection_id ? 'Disconnecting…' : 'Disconnect'}</button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  data-testid="brokerage-empty-state"
                  className="p-6 bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl"
                >
                  <div className="text-sm font-black text-[var(--app-text)] uppercase italic mb-2 text-center">
                    {pendingBrokerRequest
                      ? (STATUS_DISPLAY[pendingBrokerRequest.status] || pendingBrokerRequest.status)
                      : 'No API-enabled brokerage connected'}
                  </div>
                  <div className="text-xs font-bold text-[var(--muted-text)] leading-relaxed max-w-xl mx-auto mb-4 text-center">
                    {pendingBrokerRequest
                      ? (STATUS_BODY[pendingBrokerRequest.status] || '')
                      : 'Connect a supported paper/API trading account to enable portfolio data for your demo account. Real-money brokerage connections remain unavailable.'}
                  </div>
                  {pendingBrokerRequest ? (
                    <div
                      data-testid="brokerage-pending-request"
                      className={cn(
                        "mt-2 mx-auto max-w-xl text-left bg-[var(--card-bg)] border rounded-xl p-4",
                        STATUS_TONE[pendingBrokerRequest.status] || 'border-[var(--input-border)]'
                      )}
                    >
                      <div className="grid grid-cols-2 gap-3 text-xs font-bold text-[var(--app-text)]">
                        <div>
                          <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Provider</div>
                          <div data-testid="brokerage-pending-provider">{pendingBrokerRequest.provider}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Mode</div>
                          <div data-testid="brokerage-pending-mode">{pendingBrokerRequest.requested_mode}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Status</div>
                          <div
                            data-testid="brokerage-pending-status"
                            className={cn(
                              "font-mono",
                              (STATUS_TONE[pendingBrokerRequest.status] || '').split(' ')[0]
                            )}
                          >{pendingBrokerRequest.status}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest mb-1">Submitted</div>
                          <div data-testid="brokerage-pending-submitted-at" className="font-mono text-[11px]">{formatDateTime(pendingBrokerRequest.submitted_at || pendingBrokerRequest.updated_at)}</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <button
                        data-testid="brokerage-connect-cta"
                        type="button"
                        onClick={() => {
                          setBrokerRequestError(null);
                          setBrokerRequestLastSubmitted(null);
                          setBrokerRequestOpen(prev => !prev);
                        }}
                        aria-expanded={brokerRequestOpen}
                        className="px-5 py-3 rounded-xl bg-emerald-600 text-white text-xs font-black uppercase italic tracking-widest hover:bg-emerald-700 transition-all"
                      >
                        Connect trading account
                      </button>
                    </div>
                  )}
                  {brokerRequestOpen && !pendingBrokerRequest && (
                    <form
                      data-testid="brokerage-request-form"
                      onSubmit={(e) => { e.preventDefault(); if (brokerRequestProvider === 'alpaca_paper') { void submitAlpacaPaperConnect(); } else { void submitBrokerRequest(); } }}
                      className="mt-4 mx-auto max-w-xl text-left bg-[var(--card-bg)] border border-emerald-500/30 rounded-xl p-4 space-y-4"
                    >
                      <div className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">
                        {brokerRequestProvider === 'alpaca_paper' ? 'Self-connect Alpaca Paper' : 'Connection request'}
                      </div>
                      <div className="text-[11px] font-bold text-[var(--muted-text)] leading-relaxed">
                        {brokerRequestProvider === 'alpaca_paper'
                          ? 'Bring your own Alpaca paper API key. Credentials are sent once to the secure backend and stored encrypted. Adversyn never stores API keys in the browser. Paper-only.'
                          : 'Adversyn does not collect API keys in the browser. This request lets an admin set up a secure paper/read-only connection path. Submit your provider and mode below.'}
                      </div>
                      <div>
                        <label
                          htmlFor="broker-request-provider"
                          className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest block mb-1"
                        >Provider</label>
                        <select
                          id="broker-request-provider"
                          data-testid="brokerage-request-provider-select"
                          value={brokerRequestProvider}
                          onChange={(e) => setBrokerRequestProvider(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg bg-[var(--input-bg)] border border-[var(--input-border)] text-xs font-bold text-[var(--app-text)]"
                        >
                          {REQUESTABLE_PROVIDERS.map(p => (
                            <option key={p.id} value={p.id}>{p.label}</option>
                          ))}
                        </select>
                      </div>
                      {brokerRequestProvider !== 'alpaca_paper' && (
                      <div>
                        <label
                          htmlFor="broker-request-mode"
                          className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest block mb-1"
                        >Mode</label>
                        <select
                          id="broker-request-mode"
                          data-testid="brokerage-request-mode-select"
                          value={brokerRequestMode}
                          onChange={(e) => setBrokerRequestMode(e.target.value as 'paper' | 'read_only')}
                          className="w-full px-3 py-2 rounded-lg bg-[var(--input-bg)] border border-[var(--input-border)] text-xs font-bold text-[var(--app-text)]"
                        >
                          {REQUESTABLE_MODES.map(m => (
                            <option key={m.id} value={m.id}>{m.label}</option>
                          ))}
                        </select>
                        <div className="text-[10px] font-bold text-[var(--muted-text)] mt-1">
                          Live trading is never offered through this request route.
                        </div>
                      </div>
                      )}
                      {brokerRequestProvider !== 'alpaca_paper' && (
                      <div>
                        <label
                          htmlFor="broker-request-notes"
                          className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest block mb-1"
                        >Notes (optional)</label>
                        <textarea
                          id="broker-request-notes"
                          data-testid="brokerage-request-notes"
                          value={brokerRequestNotes}
                          onChange={(e) => setBrokerRequestNotes(e.target.value.slice(0, 1000))}
                          maxLength={1000}
                          rows={3}
                          placeholder="Optional context — never paste API keys or secrets here."
                          className="w-full px-3 py-2 rounded-lg bg-[var(--input-bg)] border border-[var(--input-border)] text-xs font-bold text-[var(--app-text)] font-mono"
                        />
                      </div>
                      )}
                      {brokerRequestProvider === 'alpaca_paper' && (
                        <div className="space-y-3 bg-[var(--input-bg)] border border-emerald-500/30 rounded-lg p-3">
                          <div className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">
                            Self-connect (paper only)
                          </div>
                          <div>
                            <label
                              htmlFor="alpaca-paper-api-key"
                              className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest block mb-1"
                            >Alpaca Paper API Key</label>
                            <input
                              id="alpaca-paper-api-key"
                              data-testid="alpaca-paper-api-key"
                              type="password"
                              autoComplete="new-password"
                              spellCheck={false}
                              autoCorrect="off"
                              autoCapitalize="off"
                              value={alpacaPaperApiKey}
                              onChange={(e) => setAlpacaPaperApiKey(e.target.value)}
                              placeholder="PK… (paper key)"
                              className="w-full px-3 py-2 rounded-lg bg-[var(--card-bg)] border border-[var(--input-border)] text-xs font-mono text-[var(--app-text)]"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="alpaca-paper-secret-key"
                              className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest block mb-1"
                            >Alpaca Paper Secret Key</label>
                            <input
                              id="alpaca-paper-secret-key"
                              data-testid="alpaca-paper-secret-key"
                              type="password"
                              autoComplete="new-password"
                              spellCheck={false}
                              autoCorrect="off"
                              autoCapitalize="off"
                              value={alpacaPaperSecretKey}
                              onChange={(e) => setAlpacaPaperSecretKey(e.target.value)}
                              placeholder="Paper secret key"
                              className="w-full px-3 py-2 rounded-lg bg-[var(--card-bg)] border border-[var(--input-border)] text-xs font-mono text-[var(--app-text)]"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="alpaca-paper-account-label"
                              className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest block mb-1"
                            >Account label (optional)</label>
                            <input
                              id="alpaca-paper-account-label"
                              data-testid="alpaca-paper-account-label"
                              type="text"
                              value={alpacaPaperAccountLabel}
                              onChange={(e) => setAlpacaPaperAccountLabel(e.target.value.slice(0, 80))}
                              maxLength={80}
                              placeholder="e.g. My Alpaca Paper"
                              className="w-full px-3 py-2 rounded-lg bg-[var(--card-bg)] border border-[var(--input-border)] text-xs font-bold text-[var(--app-text)]"
                            />
                          </div>
                          <div className="text-[10px] font-bold text-[var(--muted-text)] leading-relaxed">
                            Credentials are sent once to the secure backend and stored encrypted. Adversyn never stores API keys in the browser.
                          </div>
                          {alpacaPaperError && (
                            <div
                              data-testid="alpaca-paper-connect-error"
                              className="text-[11px] font-bold text-rose-600 bg-rose-500/10 border border-rose-500/30 rounded-lg p-2"
                            >
                              {alpacaPaperError}
                            </div>
                          )}
                          {alpacaPaperSuccess && (
                            // WO10 clean inline success (2026-06-06) — the
                            // form area previously showed a fake "Active
                            // Connection" pill from local state alone. Now
                            // we render a neutral submission acknowledgment
                            // and let the Active Connection card above
                            // (driven by /api/v1/user/broker-connections)
                            // be the single source of truth.
                            <div
                              data-testid="alpaca-paper-connect-success"
                              className="text-[11px] font-bold text-emerald-600 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2"
                            >
                              Alpaca Paper connection confirmed by Adversyn. Status: {alpacaPaperSuccess.status || 'connected'}. Key &middot;&middot;&middot;&middot;{alpacaPaperSuccess.key_last4 || '????'}. Validation: {alpacaPaperSuccess.validation_status || 'pending'}. The Active Connection card above shows the live broker record.
                            </div>
                          )}
                        </div>
                      )}
                      {brokerRequestError && (
                        <div
                          data-testid="brokerage-request-error"
                          className="text-[11px] font-bold text-rose-600 bg-rose-500/10 border border-rose-500/30 rounded-lg p-2"
                        >
                          {brokerRequestError}
                        </div>
                      )}
                      {brokerRequestLastSubmitted && (
                        <div
                          data-testid="brokerage-request-success"
                          className="text-[11px] font-bold text-emerald-600 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2"
                        >
                          Connection request submitted — provider <span className="font-mono">{brokerRequestLastSubmitted.provider}</span>{' '}
                          / mode <span className="font-mono">{brokerRequestLastSubmitted.requested_mode}</span>{' '}
                          / status <span className="font-mono">{brokerRequestLastSubmitted.status}</span>{' '}
                          / submitted <span className="font-mono">{formatDateTime(brokerRequestLastSubmitted.submitted_at)}</span>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2 justify-end">
                        <button
                          type="button"
                          data-testid="brokerage-request-cancel"
                          onClick={() => setBrokerRequestOpen(false)}
                          className="px-3 py-2 rounded-lg bg-[var(--input-bg)] border border-[var(--input-border)] text-[10px] font-black uppercase italic tracking-widest text-[var(--muted-text)] hover:text-[var(--app-text)]"
                        >Cancel</button>
                        {brokerRequestProvider === 'alpaca_paper' ? (
                          <button
                            type="submit"
                            data-testid="alpaca-paper-connect-submit"
                            disabled={alpacaPaperSubmitting || !alpacaPaperApiKey || !alpacaPaperSecretKey}
                            className={cn(
                              "px-3 py-2 rounded-lg text-[10px] font-black uppercase italic tracking-widest text-white",
                              (alpacaPaperSubmitting || !alpacaPaperApiKey || !alpacaPaperSecretKey)
                                ? "bg-emerald-700 opacity-60 cursor-wait"
                                : "bg-emerald-600 hover:bg-emerald-700"
                            )}
                          >{alpacaPaperSubmitting ? 'Connecting…' : 'Connect Alpaca Paper Account'}</button>
                        ) : (
                          <button
                            type="submit"
                            data-testid="brokerage-request-submit"
                            disabled={brokerRequestSubmitting}
                            className={cn(
                              "px-3 py-2 rounded-lg text-[10px] font-black uppercase italic tracking-widest text-white",
                              brokerRequestSubmitting
                                ? "bg-emerald-700 opacity-60 cursor-wait"
                                : "bg-emerald-600 hover:bg-emerald-700"
                            )}
                          >{brokerRequestSubmitting ? 'Submitting…' : 'Submit request'}</button>
                        )}
                      </div>
                    </form>
                  )}
                  <div className="mt-3 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest text-center">
                    Adversyn's house simulation accounts live in Simulation Lab
                    and are read-only. They are never shown here as your broker.
                  </div>
                </div>
              )}
            </div>

            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
              <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic mb-4">
                Supported API-enabled trading accounts
              </h4>
              <div
                data-testid="brokerage-supported-list"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
              >
                {SUPPORTED_BROKERS.map(broker => {
                  // WO7 isConnected exact match (2026-06-05) - prior logic
                  // matched any connection whose provider_label CONTAINED
                  // the first segment of broker.id (e.g. 'alpaca' matched
                  // both 'alpaca_paper' and 'alpaca_live'). That made the
                  // Alpaca Paper card lie as 'CONNECTED' for any user with
                  // an old WO3-era admin-provisioned-but-not-validated row.
                  // New rule: exact provider match + status='connected' AND
                  // validation must not be in the failed/disabled bucket.
                  const isConnected = userConnections.some(c => {
                    const pl = (c.provider_label || '').toLowerCase();
                    const bid = broker.id.toLowerCase();
                    const status = String(c.status || '').toLowerCase();
                    const vs = String(c.validation_status || '').toLowerCase();
                    return pl === bid
                      && status === 'connected'
                      && vs !== 'validation_failed'
                      && vs !== 'disabled';
                  });
                  const statusLabel = isConnected
                    ? 'Connected'
                    : (broker.status as any) === 'self_connect' ? 'Self-connect available'
                    : broker.status === 'coming_soon' ? 'Coming soon'
                    : broker.status === 'disabled' ? 'Disabled'
                    : 'Available';
                  return (
                    <div
                      key={broker.id}
                      data-testid={`brokerage-provider-${broker.id}`}
                      className="p-4 border border-[var(--card-border)] rounded-xl"
                    >
                      <div className="text-sm font-black text-[var(--app-text)] uppercase italic">
                        {broker.label}
                      </div>
                      <div className="mt-1 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                        {broker.mode} · {broker.method}
                      </div>
                      <div
                        data-testid={`brokerage-provider-${broker.id}-status`}
                        className={
                          'mt-2 text-[10px] font-black uppercase tracking-widest ' +
                          (isConnected ? 'text-emerald-600'
                            : broker.status === 'disabled' ? 'text-[var(--muted-text)]'
                            : 'text-amber-600')
                        }
                      >
                        {statusLabel}
                      </div>
                      <div className="mt-2 text-[11px] font-bold text-[var(--muted-text)] leading-relaxed">
                        {broker.advisory}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                Real-money brokerage connections remain unavailable unless
                specifically enabled by Adversyn admin.
              </div>
            </div>
          </div>
        );
      case 'api':
        return (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <SectionHeader title="API Integration Management" icon={Key} />
            <div className="space-y-6">
              <div className="p-6 border-2 border-dashed border-[var(--input-border)] rounded-2xl flex flex-col items-center justify-center text-center space-y-4">
                <div className="w-12 h-12 bg-[var(--input-bg)] rounded-full flex items-center justify-center text-[var(--muted-text)]">
                  <Plus size={24} />
                </div>
                <div>
                  <h4 className="text-sm font-black text-[var(--app-text)] uppercase italic">Add New API Key</h4>
                  <p className="text-xs text-[var(--muted-text)] mt-1">Connect a new brokerage or data provider</p>
                </div>
                <button 
                  onClick={() => void 0 /* alert removed */}
                  className="px-6 py-2 bg-[var(--app-text)] text-[var(--card-bg)] rounded-xl text-[10px] font-black uppercase italic hover:opacity-90 transition-all"
                >
                  Configure Key
                </button>
              </div>

              <div className="space-y-4">
                <div className="p-4 border border-[var(--card-border)] rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[var(--input-bg)] rounded-lg flex items-center justify-center text-[var(--muted-text)]">
                      <Key size={20} />
                    </div>
                    <div>
                      <div className="text-sm font-black text-[var(--app-text)] uppercase italic">Alpaca Live Key</div>
                      <div className="text-[10px] font-bold text-[var(--muted-text)] uppercase">Created: Oct 12, 2025</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-[10px] px-2 py-0.5 bg-emerald-500/10 text-emerald-600 rounded font-black uppercase">Active</span>
                    <button 
                      onClick={() => void 0 /* alert removed */}
                      className="text-[var(--muted-text)] hover:text-[var(--app-text)]"
                    >
                      <Settings size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'portfolio':
        return <PortfolioSettings profile={tradingProfile} onUpdateProfile={onUpdateTradingProfile} />;
      case 'risk':
        return (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <SectionHeader title="Trading Permissions & Risk" icon={ShieldCheck} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic border-b border-[var(--card-border)] pb-2">Safety Limits</h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Max Position Size (USD)</label>
                    {/* 2026-06-06 alpaca-paper-real-account-sync — was defaultValue="$50,000" pretending to be a real account limit. Now blank with a placeholder. */}
                    <input type="text" placeholder="Not configured" className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Max Daily Loss (USD)</label>
                    <input type="text" placeholder="Not configured" /* source-of-truth-full-ui — was defaultValue="$2,500" */ className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-[var(--muted-text)] uppercase tracking-widest">Max Leverage</label>
                    <select 
                      value={tradingProfile.maxLeverage || '4x (Standard Intraday)'}
                      onChange={(e) => {
                        logInteraction('AccountPortal', 'Change Max Leverage', { leverage: e.target.value });
                        onUpdateTradingProfile({ ...tradingProfile, maxLeverage: e.target.value });
                      }}
                      className="w-full bg-[var(--input-bg)] border border-[var(--input-border)] rounded-xl px-4 py-2 text-sm font-black text-[var(--app-text)] outline-none focus:border-emerald-600"
                    >
                      <option>1x (Cash Only)</option>
                      <option>2x</option>
                      <option>4x (Standard Intraday)</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic border-b border-[var(--card-border)] pb-2">Strategy Permissions</h4>
                <div className="space-y-3">
                  {[
                    { id: 'shortSelling', label: 'Short Selling' },
                    { id: 'options', label: 'Options Trading' },
                    { id: 'margin', label: 'Margin Trading' },
                    { id: 'afterHours', label: 'After-Hours Execution' }
                  ].map(perm => (
                    <div key={perm.id} className="flex items-center justify-between p-3 bg-[var(--input-bg)] rounded-xl">
                      <span className="text-xs font-bold text-[var(--app-text)]">{perm.label}</span>
                      <button 
                        onClick={() => setRiskPermissions(prev => ({ ...prev, [perm.id]: !prev[perm.id as keyof typeof prev] }))}
                        className={cn(
                          "w-10 h-5 rounded-full relative transition-colors",
                          riskPermissions[perm.id as keyof typeof riskPermissions] ? "bg-emerald-600" : "bg-slate-300"
                        )}
                      >
                        <div className={cn(
                          "absolute top-1 w-3 h-3 bg-white rounded-full shadow-sm transition-all",
                          riskPermissions[perm.id as keyof typeof riskPermissions] ? "right-1" : "left-1"
                        )} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'billing':
        return <BillingSubscription />;
      case 'security':
        // R4b — wires the purpose-built SecuritySettings component.
        // Previous inline security UI used hardcoded device-history fake data
        // ("London, UK" / "Just now" / "2 hours ago"); removed per the
        // "no fake account data" acceptance criterion.
        return <SecuritySettings />;
      case 'onboarding-preview':
        return <DemoOnboardingPreview />;
      case 'paper-api':
        // R4b — paper-only API connect. Component reads useAuthenticatedUser
        // internally and renders the locked-state copy when api_integrations is
        // false. The menu item is also hidden for those users via the filter.
        return <PaperApiConnect />;
      case 'admin-review':
        // R4b — admin-only review queue. Menu hidden when admin_review=false.
        return <AdminNewAccounts />;
      case 'ai':
        return (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <SectionHeader title="AI Configuration" icon={Brain} />
            <div className="space-y-8">
              <div className="space-y-4">
                <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic">Analysis Depth</h4>
                <div className="grid grid-cols-3 gap-3">
                  {['Standard', 'Deep Scan', 'Institutional'].map(level => (
                    <button 
                      key={level} 
                      onClick={() => setAnalysisDepth(level)}
                      className={cn(
                        "p-4 rounded-xl border transition-all text-center",
                        analysisDepth === level ? "border-emerald-600 bg-emerald-500/10 text-emerald-600" : "border-[var(--card-border)] text-[var(--muted-text)]"
                      )}
                    >
                      <div className="text-[10px] font-black uppercase tracking-widest">{level}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-xs font-black text-[var(--app-text)] uppercase italic">AI Personalization</h4>
                <div className="space-y-3">
                  {[
                    { id: 'strategy', label: 'Strategy Recommendations', desc: 'AI-driven trade ideas based on your risk profile.' },
                    { id: 'coaching', label: 'Portfolio Coaching', desc: 'Real-time feedback on position sizing and risk.' },
                    { id: 'sentiment', label: 'Sentiment Alerts', desc: 'Notifications for significant market sentiment shifts.' },
                  ].map(item => (
                    <div key={item.label} className="flex items-center justify-between p-4 border border-[var(--card-border)] rounded-xl">
                      <div>
                        <div className="text-sm font-black text-[var(--app-text)] uppercase italic">{item.label}</div>
                        <div className="text-[10px] text-[var(--muted-text)] font-medium">{item.desc}</div>
                      </div>
                      <button 
                        onClick={() => setAiToggles(prev => ({ ...prev, [item.id]: !prev[item.id as keyof typeof prev] }))}
                        className={cn(
                          "w-10 h-5 rounded-full relative transition-colors",
                          aiToggles[item.id as keyof typeof aiToggles] ? "bg-emerald-600" : "bg-slate-300"
                        )}
                      >
                        <div className={cn(
                          "absolute top-1 w-3 h-3 bg-white rounded-full shadow-sm transition-all",
                          aiToggles[item.id as keyof typeof aiToggles] ? "right-1" : "left-1"
                        )} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'legal':
        return (
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
            <SectionHeader title="Legal & Disclosures" icon={ScrollText} />
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {legalDocs.map((doc) => (
                  <div 
                    key={doc.title} 
                    onClick={() => setViewingDoc(doc.id)}
                    className="p-5 border border-[var(--card-border)] rounded-2xl hover:border-emerald-600 transition-all group cursor-pointer"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-black text-[var(--app-text)] uppercase italic group-hover:text-emerald-600">{doc.title}</h4>
                      <ExternalLink size={14} className="text-[var(--muted-text)] group-hover:text-emerald-600" />
                    </div>
                    <p className="text-xs text-[var(--muted-text)] mb-4">{doc.desc}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">{doc.date}</span>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewingDoc(doc.id);
                        }}
                        className="text-[9px] font-black text-emerald-600 uppercase italic hover:underline"
                      >
                        View Document
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-6 bg-slate-50 border border-slate-100 rounded-2xl">
                <div className="flex items-center gap-3 text-slate-900 mb-2">
                  <ShieldCheck size={18} className="text-emerald-600" />
                  <h4 className="text-xs font-black uppercase italic">Compliance Status</h4>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-500 font-medium">Your account is currently in good standing and all mandatory disclosures have been acknowledged.</p>
                  <div className="flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-600 rounded-lg text-[10px] font-black uppercase italic">
                    <CheckCircle2 size={12} /> Verified
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="app-container">
      <div className="flex flex-col md:flex-row gap-8">
        {/* Sidebar Navigation */}
        <aside className="w-full md:w-64 shrink-0">
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-4 shadow-sm sticky top-24">
            <button
              onClick={onBack}
              data-testid="account-btn-back"
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[var(--muted-text)] hover:bg-[var(--input-bg)] hover:text-[var(--app-text)] transition-all mb-4 group"
            >
              <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
              <span className="text-xs font-black uppercase italic tracking-tight">Back to Dashboard</span>
            </button>
            <div className="flex items-center gap-3 px-4 py-6 border-b border-[var(--card-border)] mb-4">
              <div
                data-testid="account-portal-sidebar-initials"
                className="w-12 h-12 rounded-full bg-[var(--app-text)] flex items-center justify-center text-[var(--card-bg)] font-black italic text-lg shadow-lg shadow-black/20"
              >
                {authUser.initials}
              </div>
              <div>
                <h2
                  data-testid="account-portal-sidebar-name"
                  className="text-sm font-black text-[var(--app-text)] uppercase italic"
                >
                  {(authUser.firstName + ' ' + authUser.lastName).trim() || 'Demo User'}
                </h2>
                <div
                  data-testid="account-portal-sidebar-status"
                  className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest"
                >
                  {authUser.accountStatusLabel}
                </div>
              </div>
            </div>
            <nav className="space-y-1">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  data-testid={`account-nav-${item.id}`}
                  onClick={() => {
                    if (activeSubTab === item.id) return;
                    setActiveSubTab(item.id);
                  }}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 group border",
                    activeSubTab === item.id 
                      ? "bg-emerald-600 text-white shadow-[0_0_20px_rgba(16,185,129,0.3)] border-emerald-500/50" 
                      : "text-[var(--muted-text)] hover:bg-[var(--input-bg)] hover:text-[var(--app-text)] border-transparent"
                  )}
                >
                  <item.icon size={18} className={cn("transition-all", activeSubTab === item.id ? "drop-shadow-[0_0_5px_rgba(255,255,255,0.5)]" : "group-hover:scale-110")} />
                  <span className="text-xs font-black uppercase italic tracking-tight">{item.label}</span>
                </button>
              ))}
            </nav>
            <div className="mt-8 pt-4 border-t border-[var(--card-border)]">
              <button 
                onClick={() => {
                  // In a real app, we would call auth.signOut() here
                  onLogout?.();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-rose-600 hover:bg-rose-500/10 transition-all group"
              >
                <LogOut size={18} />
                <span className="text-xs font-black uppercase italic tracking-tight">Logout</span>
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 min-w-0">
          <motion.div
            key={activeSubTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {renderSubContent()}
          </motion.div>
        </main>
      </div>

      {/* Document Viewer Modal */}
      {viewingDoc && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-6">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-md"
            onClick={() => setViewingDoc(null)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative w-full max-w-3xl bg-[var(--card-bg)] border border-[var(--card-border)] rounded-[2rem] shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
          >
            {/* Modal Header */}
            <div className="px-8 py-6 border-b border-[var(--card-header-border)] bg-[var(--card-header-bg)] flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-600">
                  <FileText size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-black text-[var(--app-text)] uppercase italic tracking-tight">
                    {legalDocs.find(d => d.id === viewingDoc)?.title}
                  </h2>
                  <p className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
                    {legalDocs.find(d => d.id === viewingDoc)?.date}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => void 0 /* alert removed */}
                  className="p-2 text-[var(--muted-text)] hover:text-emerald-600 transition-colors"
                >
                  <Download size={18} />
                </button>
                <button 
                  onClick={() => setViewingDoc(null)}
                  className="p-2 text-[var(--muted-text)] hover:text-rose-600 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-8">
              <div 
                className="prose prose-slate max-w-none 
                  prose-headings:text-[var(--app-text)] prose-headings:font-black prose-headings:uppercase prose-headings:italic prose-headings:text-sm prose-headings:mb-4 prose-headings:mt-8
                  prose-p:text-[var(--muted-text)] prose-p:text-sm prose-p:leading-relaxed prose-p:mb-4
                  first:prose-headings:mt-0"
                dangerouslySetInnerHTML={{ __html: legalDocs.find(d => d.id === viewingDoc)?.content || '' }}
              />
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-[var(--card-header-border)] bg-[var(--card-header-bg)] flex justify-end">
              <button 
                onClick={() => setViewingDoc(null)}
                className="px-8 py-3 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase italic hover:bg-emerald-500 transition-all shadow-lg shadow-emerald-600/20"
              >
                Close Document
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
